// SA-3
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { pb } from '../../../pocketbase'
import { COL_SHIFTS } from '../../../config/collections'
import { isValidShiftTime } from '../../../lib/shifts'
import type { ShiftRecord } from '../../../types/records'
import { Alert, ButtonLink, FormField, PageHeader, PageShell, formInputProps } from '../../../components/ui'
import { btn, cn, formActionsClass, formGridClass } from '../../../lib/ui'
import { pocketbaseErrorMessage } from '../../../lib/pocketbaseErrorMessage'

export function ShiftFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'new' || !id

  const [name, setName] = useState('')
  const [startTime, setStartTime] = useState('08:00')
  const [endTime, setEndTime] = useState('20:00')
  const [sort, setSort] = useState('10')
  const [active, setActive] = useState(true)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(!isNew)

  const load = useCallback(async () => {
    if (!id || isNew) return
    setLoading(true)
    setErr('')
    try {
      const row = await pb.collection(COL_SHIFTS).getOne<ShiftRecord>(id)
      setName(row.name)
      setStartTime(row.startTime)
      setEndTime(row.endTime)
      setSort(String(row.sort))
      setActive(row.active)
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка'))
    } finally {
      setLoading(false)
    }
  }, [id, isNew])

  useEffect(() => {
    void load()
  }, [load])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErr('')

    const sortVal = Number(sort)
    const nameVal = name.trim()

    if (!nameVal) {
      setErr('Укажите название смены.')
      return
    }
    if (!isValidShiftTime(startTime)) {
      setErr('Время начала — формат ЧЧ:ММ (00:00–23:59).')
      return
    }
    if (!isValidShiftTime(endTime)) {
      setErr('Время окончания — формат ЧЧ:ММ (00:00–23:59).')
      return
    }
    if (!Number.isInteger(sortVal) || sortVal < 0) {
      setErr('Порядок сортировки — целое число от 0.')
      return
    }

    const body = {
      name: nameVal,
      startTime: startTime.trim(),
      endTime: endTime.trim(),
      sort: sortVal,
      active,
    }

    try {
      if (isNew) {
        await pb.collection(COL_SHIFTS).create(body)
      } else {
        await pb.collection(COL_SHIFTS).update(id!, body)
      }
      navigate('/admin/administration/shifts', { replace: true })
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка сохранения'))
    }
  }

  return (
    <PageShell>
      <PageHeader
        title={isNew ? 'Новая смена' : 'Редактирование смены'}
        lead="Смены используются в графике производства и табеле. 2/2 — ротация Смена 1/2; 5/2 — будни для офисных и вспомогательных должностей."
      />
      {err && <Alert kind="err">{err}</Alert>}
      {loading && !isNew ? (
        <p>Загрузка…</p>
      ) : (
        <form onSubmit={(e) => void onSubmit(e)} className={cn(formGridClass, 'max-w-[40rem]')}>
          <FormField label="Название" htmlFor="shift-name">
            <input
              id="shift-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={120}
              {...formInputProps()}
            />
          </FormField>
          <FormField label="Начало (ЧЧ:ММ)" htmlFor="shift-start">
            <input
              id="shift-start"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              required
              {...formInputProps()}
            />
          </FormField>
          <FormField label="Окончание (ЧЧ:ММ)" htmlFor="shift-end">
            <input
              id="shift-end"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              required
              {...formInputProps()}
            />
          </FormField>
          <FormField label="Порядок" htmlFor="shift-sort">
            <input
              id="shift-sort"
              type="number"
              min={0}
              step={1}
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              required
              {...formInputProps()}
            />
          </FormField>
          <FormField label="Активна" htmlFor="shift-active">
            <label className="flex items-center gap-2">
              <input
                id="shift-active"
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
              <span>Смена доступна для планирования</span>
            </label>
          </FormField>
          <div className={formActionsClass}>
            <button type="submit" className={btn('primary')}>
              Сохранить
            </button>
            <ButtonLink to="/admin/administration/shifts" variant="ghost">
              Отмена
            </ButtonLink>
          </div>
        </form>
      )}
    </PageShell>
  )
}
